import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token && token !== 'null') {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

export interface LLMConfig {
  provider: string;
  name: string;
  baseUrl: string;
  model: string;
  apiKey: string;
}

/**
 * Get user's configured LLM providers
 */
export const getUserLLMProviders = async (): Promise<{
  success: boolean;
  data?: any[];
  message?: string;
}> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/llm/providers`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch LLM providers:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取LLM配置失败'
    };
  }
};

/**
 * Add or update LLM provider configuration.
 * The API key is sent as plaintext over the authenticated HTTPS request; it
 * is encrypted at rest by the server (AES-256-GCM, server-only key) and never
 * round-trips through a client-side "encryption" step. A client-side scheme
 * previously used here provided no real protection: its key was a constant
 * bundled into the public frontend build.
 */
export const saveLLMProvider = async (config: LLMConfig): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/llm/providers`,
      config,
      {
        headers: getAuthHeaders()
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Failed to save LLM provider:', error);
    return {
      success: false,
      message: error.response?.data?.message || '保存LLM配置失败'
    };
  }
};

/**
 * Delete LLM provider configuration
 */
export const deleteLLMProvider = async (providerId: string): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/llm/providers/${providerId}`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to delete LLM provider:', error);
    return {
      success: false,
      message: error.response?.data?.message || '删除LLM配置失败'
    };
  }
};

/**
 * Set active LLM provider
 */
export const setActiveLLMProvider = async (providerId: string): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/llm/providers/${providerId}/activate`,
      {},
      {
        headers: getAuthHeaders()
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Failed to set active provider:', error);
    return {
      success: false,
      message: error.response?.data?.message || '设置活跃提供商失败'
    };
  }
};

/**
 * Get active LLM configuration for API calls
 * Returns user's config or platform default. The API key is returned as
 * plaintext by the server (decrypted server-side for the authenticated
 * owner) — no client-side decryption step is needed or performed.
 */
export const getActiveLLMConfig = async (): Promise<{
  success: boolean;
  data?: any;
  usePlatformDefault?: boolean;
  message?: string;
}> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/llm/active`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error: any) {
    console.error('[LLM API] Failed to get active LLM config:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取LLM配置失败'
    };
  }
};
